import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { useQuery } from "src/hooks/APIHooks"
import {
  getDevicesByBuilding,
  getEquipmentsByBuilding
} from "src/components/shared/ReportDialog/graphql"
import { fetchAllList } from "src/stack/Queries/fetchAll"
import { selectIsHeadLess } from "src/redux/slicers/performanceSlice"

export const useActiveDeviceIds = (buildingId: string) => {
  // null = not yet fetched; Set (possibly empty) = fetched.
  // Updated synchronously inside useMemo so async callers reading the ref
  // after an await always see the latest value without a render-lag.
  const activeDeviceIdsRef = useRef<Set<string> | null>(null)
  const isHeadLess = useSelector(selectIsHeadLess)

  const {
    data: deviceList,
    loading: loadingDevices,
    refetch: refetchDevices
  } = useQuery({
    query: getDevicesByBuilding,
    dataPath: "data.listDevicesByBuilding.items",
    isHeadLess,
    disableInitialLoad: true
  })

  useEffect(() => {
    if (buildingId) {
      refetchDevices({ id: buildingId })
    }
  }, [buildingId])

  const activeDeviceIds = useMemo(() => {
    if (deviceList === null) {
      // Not fetched yet — leave ref as null
      return null
    }
    const ids = new Set<string>(
      (deviceList || []).map((device: any) => device.deviceId)
    )
    // Update ref synchronously so async consumers reading it after an await
    // always get the current value without waiting for a useEffect flush.
    activeDeviceIdsRef.current = ids
    return ids
  }, [deviceList])

  const filterByDeviceId = useCallback(
    (items) => {
      if (activeDeviceIds === null) return []
      return (
        items?.filter(
          (item) => item.deviceId && activeDeviceIds.has(item.deviceId)
        ) ?? []
      )
    },
    [activeDeviceIds]
  )

  const fetchBuildingEquipment = useCallback(
    async (bId: string, filter?: Record<string, any>) => {
      const response = await fetchAllList({
        query: getEquipmentsByBuilding,
        variables: { buildingId: bId, ...(filter ? { filter } : {}) },
        isHeadLess,
        path: "data.listEquipmentsByBuildingAndActiveAndType"
      })
      return response?.list || []
    },
    [isHeadLess]
  )

  return {
    activeDeviceIds,
    activeDeviceIdsRef,
    filterByDeviceId,
    loadingDevices,
    devicesFetched: deviceList !== null,
    fetchBuildingEquipment
  }
}

